import axios from "axios";
import { getAuthToken } from "../features/auth/AuthProvider";
import { buildErrorMessage } from "./errorMessage";

const BASE_URL = process.env.REACT_APP_API_URL;

// Fail at import time rather than letting every request 404 against the wrong host.
if (!BASE_URL) {
  throw new Error(
    "Missing required env variable: REACT_APP_API_URL. Set it in .env.development or .env.production."
  );
}

/**
 * Centralized axios instance with interceptors
 * Handles authentication, errors, and response normalization
 */
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor: adds auth token to all requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: normalizes errors and handles common cases
 */
apiClient.interceptors.response.use(
  (response) => {
    // Successful response - return data directly
    return response;
  },
  (error) => {
    const data = error.response?.data;

    // Normalize error structure
    const normalizedError = {
      message:
        buildErrorMessage(data) || error.message || "An unexpected error occurred",
      status: error.response?.status || 0,
      code: data?.code || "UNKNOWN_ERROR",
      field: data?.field || null,
      originalError: error,
    };

    // Handle network errors
    if (!error.response) {
      normalizedError.message = "Network error - please check your connection";
      normalizedError.code = "NETWORK_ERROR";
    }

    return Promise.reject(normalizedError);
  }
);

/**
 * Helper function to create standardized API methods
 */
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
};

export default apiClient;
